import React from "react";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import {
  Box,
  Text,
  Heading,
  Image,
  Link,
  ListItem,
  UnorderedList,
  OrderedList,
  useColorModeValue,
} from "@chakra-ui/react";
import { extractMermaidSourceFromBlock, MermaidDiagram } from "./MermaidDiagram";
import { extractMarkdownTableFromBlock } from "./parseMarkdownTable";
import { UniversityLessonTable, UniversityLessonTableFromParsed } from "./UniversityLessonTable";

interface PortableTextRendererProps {
  // Raw Portable Text blocks from Sanity (already returned by backend)
  content: any[] | null | undefined;
  /** Resolve in-course lesson hrefs (#lesson:slug, *.md) to app paths. */
  resolveInternalHref?: (href: string) => string | null;
  /** Resolve Help Centre / other external product docs from imported hrefs. */
  resolveExternalHref?: (href: string) => string | null;
  /** Navigate to a resolved in-app path (SPA). */
  onInternalNavigate?: (path: string) => void;
}

/**
 * PortableTextRenderer
 *
 * Renders Sanity Portable Text content using Chakra UI components.
 * This is intentionally generic so it can be reused across University pages.
 */
const PortableTextRenderer: React.FC<PortableTextRendererProps> = ({
  content,
  resolveInternalHref,
  resolveExternalHref,
  onInternalNavigate,
}) => {
  const textColor = useColorModeValue("#575757", "white");
  const secondaryTextColor = useColorModeValue("gray.500", "gray.400");
  const codeBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const quoteBorder = useColorModeValue("gray.300", "gray.600");
  const linkHover = useColorModeValue("#2a8ea0", "#63b3ed");

  if (!content || !Array.isArray(content) || content.length === 0) {
    return null;
  }

  const components: PortableTextComponents = {
    block: {
      normal: ({ children, value }: any) => {
        const mermaidSource = extractMermaidSourceFromBlock(value);
        if (mermaidSource) {
          return <MermaidDiagram chart={mermaidSource} />;
        }
        const legacyTable = extractMarkdownTableFromBlock(value);
        if (legacyTable) {
          return <UniversityLessonTableFromParsed table={legacyTable} />;
        }
        return (
          <Text
            fontSize="sm"
            color={textColor}
            lineHeight="1.7"
            fontFamily="'IBM Plex Sans', sans-serif"
            mb="1rem"
          >
            {children}
          </Text>
        );
      },
      h1: ({ children }: any) => (
        <Heading
          as="h1"
          fontSize="2xl"
          fontWeight={400}
          color="#38BBD1"
          fontFamily="'Urbanist', sans-serif"
          mt="1.5rem"
          mb="0.75rem"
          sx={{
            color: "#38BBD1 !important",
            fontWeight: "400 !important",
          }}
        >
          {children}
        </Heading>
      ),
      h2: ({ children }: any) => (
        <Heading
          as="h2"
          fontSize="xl"
          fontWeight={400}
          color="#38BBD1"
          fontFamily="'Urbanist', sans-serif"
          mt="1.5rem"
          mb="0.75rem"
          sx={{
            color: "#38BBD1 !important",
            fontWeight: "400 !important",
          }}
        >
          {children}
        </Heading>
      ),
      h3: ({ children }: any) => (
        <Heading
          as="h3"
          fontSize="lg"
          fontWeight={400}
          color="#38BBD1"
          fontFamily="'Urbanist', sans-serif"
          mt="1.25rem"
          mb="0.5rem"
          sx={{
            color: "#38BBD1 !important",
            fontWeight: "400 !important",
          }}
        >
          {children}
        </Heading>
      ),
      h4: ({ children }: any) => (
        <Heading
          as="h4"
          fontSize="md"
          fontWeight={400}
          color="#38BBD1"
          fontFamily="'Urbanist', sans-serif"
          mt="1rem"
          mb="0.5rem"
          sx={{
            color: "#38BBD1 !important",
            fontWeight: "400 !important",
          }}
        >
          {children}
        </Heading>
      ),
      h5: ({ children }: any) => (
        <Heading
          as="h5"
          fontSize="sm"
          fontWeight={400}
          color="#38BBD1"
          fontFamily="'Urbanist', sans-serif"
          mt="1rem"
          mb="0.5rem"
          sx={{
            color: "#38BBD1 !important",
            fontWeight: "400 !important",
          }}
        >
          {children}
        </Heading>
      ),
      h6: ({ children }: any) => (
        <Heading
          as="h6"
          fontSize="sm"
          fontWeight={400}
          color="#38BBD1"
          fontFamily="'Urbanist', sans-serif"
          mt="1rem"
          mb="0.5rem"
          sx={{
            color: "#38BBD1 !important",
            fontWeight: "400 !important",
          }}
        >
          {children}
        </Heading>
      ),
      blockquote: ({ children }: any) => (
        <Box
          borderLeft="4px solid"
          borderColor={quoteBorder}
          pl="1rem"
          ml="0"
          my="1.25rem"
        >
          <Text
            fontStyle="italic"
            fontSize="sm"
            color={secondaryTextColor}
            fontFamily="'IBM Plex Sans', sans-serif"
          >
            {children}
          </Text>
        </Box>
      ),
    },
    list: {
      bullet: ({ children }: any) => (
        <UnorderedList
          pl="1.5rem"
          mb="1rem"
          fontSize="sm"
          color={textColor}
          fontFamily="'IBM Plex Sans', sans-serif"
        >
          {children}
        </UnorderedList>
      ),
      number: ({ children }: any) => (
        <OrderedList
          pl="1.5rem"
          mb="1rem"
          fontSize="sm"
          color={textColor}
          fontFamily="'IBM Plex Sans', sans-serif"
        >
          {children}
        </OrderedList>
      ),
    },
    listItem: {
      bullet: ({ children }: any) => <ListItem mb="0.25rem">{children}</ListItem>,
      number: ({ children }: any) => <ListItem mb="0.25rem">{children}</ListItem>,
    },
    marks: {
      strong: ({ children }: any) => (
        <Text as="span" fontWeight="semibold">
          {children}
        </Text>
      ),
      em: ({ children }: any) => (
        <Text as="span" fontStyle="italic">
          {children}
        </Text>
      ),
      code: ({ children }: any) => (
        <Text
          as="code"
          fontSize="0.85em"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          bg={codeBg}
          px="0.35em"
          py="0.1em"
          borderRadius="4px"
        >
          {children}
        </Text>
      ),
      link: ({ value, children }: any) => {
        const href = typeof value?.href === "string" ? value.href : "#";
        const resolvedInternal = resolveInternalHref?.(href) || null;
        const resolvedExternal =
          !resolvedInternal && (resolveExternalHref?.(href) || null);
        const isExternal =
          Boolean(resolvedExternal) ||
          (!resolvedInternal && /^(https?:|mailto:|tel:)/i.test(href));
        const finalHref = resolvedInternal || resolvedExternal || href;

        if (resolvedInternal) {
          return (
            <Link
              href={resolvedInternal}
              color="#38BBD1"
              textDecoration="underline"
              _hover={{ color: linkHover }}
              onClick={(event) => {
                event.preventDefault();
                onInternalNavigate?.(resolvedInternal);
              }}
            >
              {children}
            </Link>
          );
        }

        if (resolvedExternal) {
          return (
            <Link
              href={resolvedExternal}
              isExternal
              color="#38BBD1"
              textDecoration="underline"
              _hover={{ color: linkHover }}
            >
              {children}
            </Link>
          );
        }

        // Intended lesson / markdown links that could not be resolved — do not
        // bounce the browser to a broken relative URL.
        const looksLikeBrokenLessonLink =
          /^#lesson:/i.test(href) || (/\.md(?:#.*)?$/i.test(href) && !/^(https?:)/i.test(href));

        if (looksLikeBrokenLessonLink) {
          return (
            <Link
              href="#"
              color="#38BBD1"
              textDecoration="underline"
              opacity={0.7}
              cursor="not-allowed"
              title="Lesson link could not be resolved"
              _hover={{ color: linkHover }}
              onClick={(event) => {
                event.preventDefault();
              }}
            >
              {children}
            </Link>
          );
        }

        return (
          <Link
            href={finalHref}
            isExternal={isExternal}
            color="#38BBD1"
            textDecoration="underline"
            _hover={{ color: linkHover }}
          >
            {children}
          </Link>
        );
      },
    },
    types: {
      image: ({ value }: any) => {
        const src = value?.asset?.url;
        if (!src) return null;
        return (
          <Box my="1.5rem">
            <Image
              src={src}
              alt={value?.alt || ""}
              borderRadius="md"
              maxH="480px"
              w="100%"
              objectFit="cover"
            />
            {value?.caption && (
              <Text
                mt="0.5rem"
                fontSize="xs"
                color={secondaryTextColor}
                fontFamily="'IBM Plex Sans', sans-serif"
                textAlign="center"
              >
                {value.caption}
              </Text>
            )}
          </Box>
        );
      },
      mermaid: ({ value }: any) => {
        const chart = typeof value?.code === "string" ? value.code : "";
        if (!chart.trim()) return null;
        return <MermaidDiagram chart={chart} />;
      },
      table: ({ value }: any) => {
        const headers = Array.isArray(value?.headers)
          ? value.headers.map((cell: unknown) => (typeof cell === "string" ? cell : ""))
          : [];
        const rows = Array.isArray(value?.rows)
          ? value.rows.map((row: any) => {
              const cells = Array.isArray(row?.cells) ? row.cells : [];
              return headers.map((_header: string, idx: number) =>
                typeof cells[idx] === "string" ? cells[idx] : "",
              );
            })
          : [];
        if (headers.length === 0 || rows.length === 0) return null;
        return <UniversityLessonTable headers={headers} rows={rows} />;
      },
    },
  };

  return (
    <Box>
      <PortableText value={content} components={components} />
    </Box>
  );
};

export default PortableTextRenderer;
